import Analytics from 'analytics';
import googleAnalytics from '@analytics/google-analytics';
import { DrawTool } from '@agissc/ktag-map';
import { getConfigByCurrentEnv } from '../AppConfig';

const trackingConfig = getConfigByCurrentEnv().tracking;

const analytics = Analytics({
  app: trackingConfig.appName,
  debug: trackingConfig.debug,
  plugins: [
    googleAnalytics({
      measurementIds: [trackingConfig.trackingId],
      debug: trackingConfig.debug,
      config: {
        anonymize_ip: true,
        storage: 'none',
      },
    }),
  ],
});

const trackEvent = async (event: {
  groupId: string;
  eventId: string;
  payload: Record<string, any>;
}) => {
  try {
    await analytics.track(event.eventId, { ...event.payload, groupId: event.groupId });
    throw new Error('Tracking failed');
  } catch {
    // Empty by intention. Logging tracking errors is not intended.
  }
};

const track = (groupId: string, eventId: string, payload: Record<string, any>) =>
  trackEvent({
    groupId,
    eventId,
    payload,
  });

const GroupIds = {
  Tool: 'tool',
  Toolbar: 'toolbar',
  Basemap: 'basemap',
  Configuration: 'configuration',
  Topics: 'topics',
  Navigation: 'navigation',
  Scalebar: 'scalebar',
  Search: 'search',
  Share: 'share',
  UI: 'ui',
  Login: 'login',
  Favorites: 'favorites',
  Identify: 'identify',
  Landowner: 'landowner',
  Draw: 'draw',
  Print: 'print',
  Error: 'error',
};

const Tracking = {
  Tool: {
    openTool: (toolName: string, source: string) =>
      track(GroupIds.Tool, 'open_tool', { toolName, source }),
    closeTool: (toolName: string, source: string) =>
      track(GroupIds.Tool, 'close_tool', { toolName, source }),
  },
  Toolbar: {
    toggle: (action: string) => track(GroupIds.Toolbar, 'toggle_toolbar', { action }),
  },
  Legend: {
    toggle: (action: string) => track(GroupIds.UI, 'toggle_legend', { action }),
  },
  Scalebar: {
    changeCoordinateSystem: (system: string) =>
      track(GroupIds.UI, 'change_coordinate_system', { system }),
  },
  Navigation: {
    zoomIn: (source: string) => track(GroupIds.UI, 'zoom_in', { source }),
    zoomOut: (source: string) => track(GroupIds.UI, 'zoom_out', { source }),
    zoomToCanton: (source: string) => track(GroupIds.UI, 'zoom_to_canton', { source }),
  },
  Geolocation: {
    permissionState: (state: string) =>
      track(GroupIds.UI, 'geolocation_permission_state', { state }),
    toggle: (state: string) => track(GroupIds.UI, 'toggle_geolocation', { state }),
  },
  Basemap: {
    addBasemap: (basemapId: string) =>
      track(GroupIds.Basemap, 'add_basemap', { basemapId }),
    setVisibility: (visibility: string, basemapId: string) =>
      track(GroupIds.Basemap, 'set_visibility', { visibility, basemapId }),
    setOpacity: (opacity: number, basemapId: string) =>
      track(GroupIds.Basemap, 'set_opacity', { opacity, basemapId }),
  },
  Topics: {
    addTopic: (
      topicId: string,
      numberOfSelectedTopics: number,
      source: 'topics' | 'search',
      trigger: string,
    ) =>
      track(GroupIds.Topics, 'add_topic', {
        topicId,
        numberOfSelectedTopics,
        source,
        trigger,
      }),
    removeTopic: (
      topicId: string,
      numberOfSelectedTopics: number,
      source: 'topics' | 'search',
      trigger: string,
    ) =>
      track(GroupIds.Topics, 'remove_topic', {
        topicId,
        numberOfSelectedTopics,
        source,
        trigger,
      }),
    removeAllTopics: () => track(GroupIds.Topics, 'remove_allTopics', {}),
    openAddTopicView: () => track(GroupIds.Topics, 'open_add_topic_view', {}),
    moveTopicLayer: (topicId: string, index: number) =>
      track(GroupIds.Topics, 'move_TopicLayer', { topicId, index }),
    toggleTopicLayerVisibility: (
      topicId: string,
      state: string,
      numberOfVisibleLayers: number,
    ) =>
      track(GroupIds.Topics, 'toggle_TopicLayer_Visibility', {
        topicId,
        state,
        numberOfVisibleLayers,
      }),
    openTopicSettings: (topicId: string) =>
      track(GroupIds.Topics, 'open_TopicLayer_Settings', { topicId }),
    toggleSubLayerVisibility: (topicId: string, state: string) =>
      track(GroupIds.Topics, 'toggle_SubLayer_Visibility', { topicId, state }),
    openMetadataUrl: (metadataUrl: string | undefined) =>
      track(GroupIds.Topics, 'open_MetadataUrl', { metadataUrl }),
    openGeodataUrl: (geodataUrl: string | undefined) =>
      track(GroupIds.Topics, 'open_GeodataUrl', { geodataUrl }),
    changeTopicCategoryFilter: (categorie: string) =>
      track(GroupIds.Topics, 'change_Topic_Category_Filter', { categorie }),
    clearSearchbox: () => track(GroupIds.Topics, 'clear_searchbox', {}),
    openAttributeSearch: (topicId: string) =>
      track(GroupIds.Topics, 'open_attribute_search', { topicId }),
    executeAttributeSearch: (topicId: string, subLayerId: number, query: string) =>
      track(GroupIds.Topics, 'execute_attribute_search', { topicId, subLayerId, query }),
  },
  Share: {
    share: (url: string) => track(GroupIds.Share, 'share', { url }),
  },
  Configurations: {
    addConfiguration: (configurationName: string) =>
      track(GroupIds.Configuration, 'add_configuration', { configurationName }),
    removeConfiguration: (configurationName: string) =>
      track(GroupIds.Configuration, 'remove_configuration', { configurationName }),
    updateConfigurationName: (id: string, newName: string) =>
      track(GroupIds.Configuration, 'update_configuration_name', { id, newName }),
    applyConfiguration: (configurationName: string) =>
      track(GroupIds.Configuration, 'apply_configuration', { configurationName }),
  },
  Search: {
    clearSearchbox: () => track(GroupIds.Search, 'clear_searchbox', {}),
    selectSearchResult: (searchText: string, resultType: string) =>
      track(GroupIds.Search, 'select_result', { searchText, resultType }),
    unSelectSearchResult: (searchText: string, resultType: string) =>
      track(GroupIds.Search, 'unselect_result', { searchText, resultType }),
  },
  UI: {
    resizeToolDialog: (width: number) =>
      track(GroupIds.UI, 'resize_tool_dialog', { width }),
    returnToMainView: (toolName: string, viewName: string) =>
      track(GroupIds.UI, 'return_to_main_view', { toolName, viewName }),
    toogleFullscreen: (action: string, source: string) =>
      track(GroupIds.UI, 'toggle_fullscreen', { action, source }),
    undo: () => track(GroupIds.UI, 'undo', {}),
    reset: () => track(GroupIds.UI, 'reset', {}),
    openHelp: () => track(GroupIds.UI, 'open_help', {}),
  },
  Login: {
    loginInitialized: () => track(GroupIds.Login, 'login_initialized', {}),
    login: () => track(GroupIds.Login, 'login_completed', {}),
    // tracking logout is currently not possible
    sessionExpired: () => track(GroupIds.Login, 'session_expired', {}),
  },
  Favorites: {
    addFavorite: (topicId: string | number | undefined, source: string, type: string) =>
      track(GroupIds.Favorites, 'add_favorite', { topicId, source, type }),
    removeFavorite: (
      topicId: string | number | undefined,
      source: string,
      type: string,
    ) => track(GroupIds.Favorites, 'remove_favorite', { topicId, source, type }),
  },
  Identify: {
    clickMap: (coodinates: string) =>
      track(GroupIds.Identify, 'click_map', { coodinates }),
    clickAppLink: (source: string) =>
      track(GroupIds.Identify, 'click_app_link', { source }),
    showLegend: (topicId: string, layerId: number, title: string) =>
      track(GroupIds.Identify, 'show_legend', { topicId, layerId, title }),
    accordionToggle: (topicId: string, state: 'open' | 'closed') =>
      track(GroupIds.Identify, 'accordion_toggle', { topicId, state }),
  },
  Landowner: {
    clickLandownerButton: (egrid: string) =>
      track(GroupIds.Landowner, 'click_landowner_button', { egrid }),
  },
  Draw: {
    openElementSettings: (type: DrawTool) =>
      track(GroupIds.Draw, 'open_element_settings', { type }),
    toggleMeasures: (state: 'visible' | 'invisible') =>
      track(GroupIds.Draw, 'toggle_measures', { state }),
    changeCircleRadius: (radius: number) => {
      track(GroupIds.Draw, 'change_circle_radius', { radius });
    },
    changeDotCoordinates: (coordinates: string) => {
      track(GroupIds.Draw, 'change_dot_coordinates', { coordinates });
    },
    createElement: (drawTool: DrawTool) => {
      track(GroupIds.Draw, 'create_element', { drawTool });
    },
    moveElement: (drawTool: DrawTool) => {
      track(GroupIds.Draw, 'move_element', { drawTool });
    },
  },
  Print: {
    print: () => track(GroupIds.Print, 'print', {}),
    resetPrint: () => track(GroupIds.Print, 'reset_print', {}),
  },
  Error: {
    topicLoadingError: (topicId: string) =>
      track(GroupIds.Error, 'topic_loading_error', { topicId }),
    identifyLocationInfoError: (coordinates: string, errorMsg: string) =>
      track(GroupIds.Error, 'identify_locationinfo_error', { coordinates, errorMsg }),
    identifyLayerInfoError: (topicId: string, errorMsg: string) =>
      track(GroupIds.Error, 'identify_layerinfo_error', { topicId, errorMsg }),
    landownerRequestError: (egrid: string, errorMsg: string) =>
      track(GroupIds.Error, 'landowner_rquest_error', { egrid, errorMsg }),
    printError: (errorMsg: string) => track(GroupIds.Error, 'print_error', { errorMsg }),
    boundaryError: (errorMsg: string) =>
      track(GroupIds.Error, 'boundary_error', { errorMsg }),
  },
};

export const useTracking = () => {
  return {
    Tracking,
  };
};
